import React, { useCallback, useEffect, useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { useStoreState } from "ezStore";
import { gql } from "__generated__/gql";
import { useApolloClient, useMutation } from "@apollo/client";

import { Button, Checkbox, FormGroup, Input, Select, Table } from "@cofano/cck";
import RelationSelect from "view/relations/components/selects/RelationSelect";
import CurrencySelect from "components/selects/CurrencySelect";
import CostTypeSelect from "view/costtypes/components/selects/CostTypeSelect";
import FormField from "components/cck/FormField";
import { FormDialog } from "components/dialogs/Dialogs";
import {
	Box,
	Tooltip,
	ToggleButton,
	ToggleButtonGroup,
} from "@mui/material";
import Edit from "@mui/icons-material/Edit";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";

import { DataFormat, Language, usePermission } from "imports/Utils";

import DataType from "components/utils/DataType";
import { useConfirm } from "components/hooks/useConfirm";
import moment from "moment";

import { normalizeUnit } from "./Declarations";
import { faCheckCircle, faTimesCircle } from "@fortawesome/pro-light-svg-icons";
import shortid from "shortid";
import { DeclareWithRemarkDocument } from "__generated__/view/declarations/graphql/declaration.mutations.generated";
import { RelationLink } from "utils/LinkUtils";
import { CharterType, ContractStatus } from "__generated__/enums.ts";
import ApproveInvoiceLineDialog from "view/invoicing/components/dialogs/ApproveInvoiceLineDialog";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const InvoiceLines = ({ charter, loading, refetch, disableRowSelection, window, deletable }) => {

	const tableRef = useRef();
	const declarationIdRef = useRef();

	const confirm = useConfirm();

	const { locale } = useStoreState(store => store.session);

	const { isAdmin, isCompanyUser, isFinance, isReadOnly, isFinanceAdmin } = usePermission();

	const canApprove = isAdmin || isFinance;
	const canRevertApprove = isAdmin || isFinanceAdmin;
	const canMarkFinanciallyReady = isAdmin || isFinance;
	const canMarkFinanciallyUnready = isAdmin || isFinance;
	const canClose = isAdmin || isFinanceAdmin;
	const canDelete = isAdmin;

	const [ closeDeclaration, { loading: closeLoading } ] = useMutation(closeMutation, {
		onCompleted: () => reload(),
	});

	const [ financiallyReadyDeclaration, { loading: financiallyReadyLoading } ] = useMutation(FINANCIALLY_READY_MUTATION, {
		onCompleted: () => reload(),
	});

	const [ financiallyUnReadyDeclaration, { loading: financiallyUnReadyLoading } ] = useMutation(FINANCIALLY_UNREADY_MUTATION, {
		onCompleted: () => reload(),
	});

	const [ declare, { loading: declareLoading } ] = useMutation(DeclareWithRemarkDocument, {
		onCompleted: () => reload(),
	});

	const [ deleteInvoiceline ] = useMutation(DELETE_LINE, {
		onCompleted: () => refetch(),
	});

	const [ revokeApproval ] = useMutation(REVOKE_APPROVAL, {
		onCompleted: () => refetch(),
	});

	const [ deleteDeclaration ] = useMutation(DELETE_DECLARATION, {
		onCompleted: () => refetch(),
	});

	const [ state, setState ] = useState({
		modalOpen: false,
		invoiceLine: undefined,
	});

	const [ approveDialog, setApproveDialog ] = useState({
		open: false,
		invoiceLineIds: [],
	});

	const clearSelection = () => {
		if (tableRef.current) {
			tableRef.current.clearSelection();
		}
	};

	const buildCloseChecklist = () => {
		const checklist = [];
		checklist.push({
			label: intl.formatMessage({ id: "ALL_INVOICELINES_APPROVED" }),
			value: charter?.declarations?.[0]?.invoiceLines?.length > 0 ? charter?.declarations?.[0]?.invoiceLines?.every(l => l.approved) : true,
		});

		checklist.push({
			label: intl.formatMessage({ id: "ALL_VOYAGES_HAVE_SCHEDULES" }),
			value: charter?.voyages?.every(v => v.schedule),
		});

		if (checklist.every(c => c.value)) {
			return;
		}

		return (
			<div className="checklist-popover">
				{checklist.map((c, index) => (
					<div key={index}>
						<span>{c.label}</span>
						<span>
							<FontAwesomeIcon icon={c.value ? faCheckCircle : faTimesCircle} />
						</span>
					</div>
				))}
			</div>
		);
	};

	const buildDeclareInput = () => {
		return {
			charterId: charter.id,
			from: moment(window.from).unix() * 1000,
			to: moment(window.to).unix() * 1000,
			shipId: charter.ships?.[0]?.id,
		};
	};

	const reload = () => {
		clearSelection();
		refetch();
	};

	useEffect(() => {
		if (charter?.declarations?.[0]?.id) {
			declarationIdRef.current = charter.declarations[0].id;
		} else {
			declarationIdRef.current = undefined;
		}
	}, [ charter?.declarations?.[0]?.id ]);

	let invoiceLines = charter?.declarations?.[0]?.invoiceLines || [];

	const isDeclarationClosed = charter?.declarations?.[0]?.closedOn;
	const isFinanciallyReady = charter?.declarations?.[0]?.financiallyReadiedOn;
	const disabled = isDeclarationClosed || isReadOnly;
	const checklist = buildCloseChecklist();
	const canBeClosed = !disabled && !checklist;
	const canEdit = !isFinanciallyReady && (isAdmin || isCompanyUser || isFinance);

	const buildRowActions = () => {
		const actions = [];

		if (!disabled) {
			if (canRevertApprove) {
				actions.push({
					icon: <RotateLeftIcon />,
					tooltip: intl.formatMessage({ id: "INVOICE_LINE_WITHDRAW_APPROVAL" }),
					disabled: (row) => !row.approved,
					onClick: (row) => {
						revokeApproval({
							variables: {
								invoiceLineIds: [ row.id ],
							},
						});
					},
				});
			}

			if (canEdit) {
				actions.push({
					icon: <Edit />,
					hidden: row => row.approved,
					onClick: (row) => {
						setState(oldState => ({
							...oldState,
							invoiceLine: row,
							modalOpen: true,
						}));
					},
				});
			}

			if (deletable && canEdit) {
				actions.push({
					icon: <DeleteOutline />,
					hidden: row => row.approved,
					onClick: (row) => {
						confirm({
							type: "DELETE",
						}).then(() => {
							deleteInvoiceline({
								variables: {
									id: row.id,
								},
							});
						});
					},
				});
			}
		}

		return actions;
	};

	const buildTableActions = () => {
		const tableActions = [];

		if (disableRowSelection || disabled) {
			return [];
		} else {
			if (!isFinanciallyReady) {
				tableActions.push({
					render: () => (
						<Button
							onClick={() => setState(oldState => ({ ...oldState, modalOpen: true }))}
							variant="outlined"
							size="small">
							{intl.formatMessage({ id: "ADD_BUTTON" })}
						</Button>
					),
				});
			}

			if (canApprove) {
				tableActions.push({
					title: Language("INVOICE_LINES_APPROVE"),
					onClick: (selection) => {
						setApproveDialog({
							open: true,
							invoiceLineIds: selection.map(i => i.id),
						});
					},
				});
			}

			if (!isFinanciallyReady && canMarkFinanciallyReady) {
				tableActions.push({
					render: () => (
						<Button
							onClick={() => {
								confirm({
									type: "WARNING",
									title: intl.formatMessage({ id: "DECLARATION_FINANCIAL_READY_CONFIRM" }),
								}).then(() => {
									if (declarationIdRef.current) {
										financiallyReadyDeclaration({
											variables: {
												id: declarationIdRef.current,
											},
										});
									} else {
										declare({
											variables: {
												input: buildDeclareInput(),
											},
										}).then(response => {
											financiallyReadyDeclaration({
												variables: {
													id: response.data?.declare?.id,
												},
											});
										});
									}
								});
							}}
							loading={financiallyReadyLoading || declareLoading}
							size="small"
							variant="outlined">
							{Language("READY_FINANCIALLY")}
						</Button>
					),
				});
			}

			if (isFinanciallyReady && canMarkFinanciallyUnready) {
				tableActions.push({
					render: () => <Button
						onClick={() => {
							if (declarationIdRef.current) {
								financiallyUnReadyDeclaration({
									variables: {
										id: declarationIdRef.current,
									},
								});
							} else {
								declare({
									variables: {
										input: buildDeclareInput(),
									},
								}).then(response => {
									financiallyUnReadyDeclaration({
										variables: {
											id: response.data?.declare?.id,
										},
									});
								});
							}
						}}
						loading={financiallyUnReadyLoading || declareLoading}
						size="small"
						variant="outlined">
						{Language("UNREADY_FINANCIALLY")}
					</Button>,
				});
			}

			if (canClose) {
				tableActions.push({
					render: () => (
						<Tooltip title={checklist}>
							<span>
								<Button
									onClick={() => {
										confirm({
											type: "WARNING",
											title: intl.formatMessage({ id: "DECLARATION_CLOSE_CONFIRM" }),
										}).then(() => {
											if (declarationIdRef.current) {
												closeDeclaration({
													variables: {
														id: declarationIdRef.current,
													},
												});
											} else {
												declare({
													variables: {
														input: buildDeclareInput(),
													},
												}).then(response => {
													closeDeclaration({
														variables: {
															id: response.data?.declare?.id,
														},
													});
												});
											}

										});
									}}
									loading={closeLoading || declareLoading}
									disabled={!canBeClosed || closeLoading || declareLoading}
									size="small"
									variant="outlined">
									{Language("CLOSE")}
								</Button>
							</span>
						</Tooltip>
					),
				});
			}

			if (canDelete && charter?.declarations?.[0]?.id) {
				tableActions.push({
					render: () => (
						<Button
							onClick={() => {
								confirm({
									type: "WARNING",
									title: intl.formatMessage({ id: "DECLARATION_DELETE_CONFIRM" }),
								}).then(() => {
									deleteDeclaration({
										variables: {
											id: declarationIdRef.current,
										},
									});
								});
							}}
							color="danger"
							size="small"
							variant="outlined">
							{Language("DELETE_DECLARATION")}
						</Button>
					),
				});
			}
		}

		return tableActions;
	};

	return (
		<React.Fragment>
			<Table
				id="declarationInvoiceLines"
				defaultSize="COMPACT"
				tableRef={tableRef}
				contentLoading={loading}
				key={shortid.generate()}
				content={invoiceLines}
				columns={[ {
					id: "costType",
					title: intl.formatMessage({ id: "COST_TYPE" }),
					render: (row, field) => {
						if (field) {
							return DataFormat.costType(field, locale);
						}
						const agreement = charter?.contract?.agreements?.find(a => a.id === row.agreement?.id);
						if (agreement?.type) {
							return intl.formatMessage({ id: `AGREEMENT_TYPE_${agreement.type}` });
						}
						return "-";
					},
					width: 70,
				}, {
					id: "quantity",
					title: intl.formatMessage({ id: "INVOICE_QUANTITY" }),
					render: (row) => row.unit === "NONE"
						? `${row.quantity}x`
						: row.unit === "%"
							? `${(row.quantity * 100).toFixed(0)}%`
							: DataFormat.simpleUnit(row.quantity, normalizeUnit(row.unit)),
					width: 70,
				}, {
					id: "price",
					title: intl.formatMessage({ id: "INVOICE_PRICE" }),
					render: (row) => DataFormat.price(row.price?.value, row.price?.currency?.isoCode),
					width: 70,
				}, {
					id: "totalPrice",
					title: intl.formatMessage({ id: "TOTAL_EXCLUDE_VAT" }),
					render: (row) => DataFormat.price(row.totalPriceExcVAT, row.price?.currency?.isoCode),
					width: 70,
				}, {
					id: "debtor",
					title: intl.formatMessage({ id: "DEBTOR" }),
					render: (row) => <RelationLink relation={row.debtor} />,
					width: 70,
				}, {
					id: "creditor",
					title: intl.formatMessage({ id: "CREDITOR" }),
					render: (row) => <RelationLink relation={row.creditor} />,
					width: 70,
				}, {
					id: "approved",
					title: intl.formatMessage({ id: "INVOICE_APPROVED" }),
					width: 70,
					render: (row) => DataFormat.boolean(row.approved),
				}, {
					id: "invoiceReference",
					title: intl.formatMessage({ id: "INVOICE_REFERENCE" }),
					width: 70,
				}, {
					id: "description",
					title: intl.formatMessage({ id: "DESCRIPTION" }),
					width: 70,
				}, {
					id: "unexpected",
					title: intl.formatMessage({ id: "UNEXPECTED" }),
					render: (row, field) => DataFormat.boolean(field ? true : undefined),
					width: 70,
				} ]}
				rowActions={buildRowActions()}
				actions={buildTableActions()}
				selectSettings={{
					selectionEnabled: !disableRowSelection,
				}} />

			<ApproveInvoiceLineDialog
				open={approveDialog.open}
				onClose={() => {
					setApproveDialog({
						open: false,
						invoiceLineIds: [],
					});
				}}
				invoiceLineIds={approveDialog.invoiceLineIds}
				refetch={reload} />

			{state.modalOpen && (
				<InvoiceLineModal
					open={state.modalOpen}
					onClose={() => {
						setState(oldState => ({
							...oldState,
							modalOpen: false,
							invoiceLine: undefined,
						}));
					}}
					charter={charter}
					window={window}
					locale={locale}
					invoiceLine={state.invoiceLine}
					refetch={reload} />
			)}
		</React.Fragment>
	);
};

const InvoiceLineModal = ({ open, onClose, charter: charterProp, locale, invoiceLine, refetch, window: { from, to } = {} }) => {

	const [ saveInvoiceLine, { loading: saveLoading } ] = useMutation(mutation);
	const [ declare ] = useMutation(DeclareWithRemarkDocument);
	const { currencies, settings: { multiValues: { contractCurrencies } }  } = useStoreState(store => store.session);
	const [ charter ] = useState(charterProp);
	const currentCurrency = invoiceLine?.price?.currency?.isoCode || charter?.declarations?.[0]?.invoiceLines?.[0]?.price?.currency?.isoCode;

	const { defaultIsoCode, defaultSymbol } = getCurrencyDefaults(invoiceLine, charter, contractCurrencies, currencies);

	const client = useApolloClient();

	const isEditing = !!invoiceLine?.id;
	const [ mode, setMode ] = useState("manual");
	const [ selectedSupplier, setSelectedSupplier ] = useState(null);
	const [ selectedContract, setSelectedContract ] = useState(null);
	const [ selectedAgreement, setSelectedAgreement ] = useState();
	const [ manualCreditor, setManualCreditor ] = useState(invoiceLine?.creditor || null);
	const [ currencySymbol, setCurrencySymbol ] = useState(defaultSymbol || "€");

	const defaultCurrency = currencies?.find(c => c.isoCode === defaultIsoCode) || null;

	const { control, handleSubmit, setValue, getValues, watch, formState, reset } = useForm({
		defaultValues: {
			costType: invoiceLine?.costType ?? null,
			description: invoiceLine?.description ?? (invoiceLine?.costType ? DataFormat.costType(invoiceLine.costType, locale) : ""),
			quantity: invoiceLine?.quantity ?? undefined,
			priceValue: invoiceLine?.price?.value ?? undefined,
			totalPrice: invoiceLine ? (invoiceLine.price?.value || 0) * (invoiceLine.quantity || 0) : 0,
			currency: defaultCurrency,
			unexpected: invoiceLine?.unexpected ?? false,
			supplier: null,
			contract: null,
			agreement: null,
		},
	});

	const costType = watch("costType");

	const filteredAgreements = (selectedContract?.agreements || [])
		.filter(agreement => agreement?.costType?.id === costType?.id);

	const fetchContracts = useCallback(async () => {
		if (!selectedSupplier?.id) {
			return { options: [], totalResults: 0 };
		}
		const result = await client.query({
			query: CONTRACTS_QUERY,
			variables: {
				search: {
					creditors: [ selectedSupplier.id ],
					validOver: {
						from: moment(from).unix() * 1000,
						to: moment(to).unix() * 1000,
					},
					status: [ ContractStatus.Accepted, ContractStatus.Active, ContractStatus.Finished ],
					charterType: CharterType.Purchase,
				},
				pagination: { page: 0, size: 100 },
			},
		});
		const content = result.data?.contracts?.content || [];
		return { options: content, totalResults: content.length };
	}, [ selectedSupplier?.id ]);

	const fetchAgreements = useCallback(async () => {
		return { options: filteredAgreements, totalResults: filteredAgreements.length };
	}, [ selectedContract?.id, costType ]);

	const handleModeChange = (newMode) => {
		setMode(newMode);
		setSelectedAgreement(undefined);
		setSelectedContract(null);
		setSelectedSupplier(null);
		setManualCreditor(null);
		reset({
			costType,
			description: costType ? DataFormat.costType(costType, locale) : "",
			quantity: undefined,
			priceValue: undefined,
			totalPrice: 0,
			currency: defaultCurrency,
			unexpected: false,
			supplier: null,
			contract: null,
			agreement: null,
		});
	};

	const handleCostTypeChange = (newCostType) => {
		setValue("costType", newCostType, { shouldValidate: true });
		setValue("description", newCostType ? DataFormat.costType(newCostType, locale) : "");
		setSelectedAgreement(undefined);
		setValue("agreement", null);
	};

	const handleSupplierSelect = (supplier) => {
		setSelectedSupplier(supplier);
		setValue("supplier", supplier, { shouldValidate: true });
		setSelectedContract(null);
		setValue("contract", null);
		setSelectedAgreement(undefined);
		setValue("agreement", null);
	};

	const handleContractSelect = (contract) => {
		setSelectedContract(contract);
		setValue("contract", contract, { shouldValidate: true });
		setSelectedAgreement(undefined);
		setValue("agreement", null);
	};

	const handleAgreementSelect = (agreement) => {
		if (agreement) {
			setValue("priceValue", agreement.calculationMethod?.price || 0);
			setValue("description", costType ? DataFormat.costType(costType, locale) : "");
		}
		setSelectedAgreement(agreement);
		setValue("agreement", agreement, { shouldValidate: true });
	};

	const handleTotalPrice = (quantity, price) => {
		if (!isNaN(Number(quantity)) && !isNaN(Number(price))) {
			const totalPrice = DataType.priceWithQuantity(price, quantity);
			const formattedTotalPrice = totalPrice ? Number(totalPrice).toFixed(2) : undefined;
			setValue("totalPrice", formattedTotalPrice);
		}
	};

	const save = async (data) => {
		let declarationId;
		if (charter?.declarations?.[0]?.id) {
			declarationId = charter.declarations[0].id;
		} else {
			await declare({
				variables: {
					input: {
						charterId: charter.id,
						from: moment(from).unix() * 1000,
						to: moment(to).unix() * 1000,
						shipId: charter.ships?.[0]?.id,
					},
				},
			}).then(response => {
				declarationId = response.data.declare.id;
			});
		}

		let creditorInput;
		if (mode === "supplier" && selectedContract) {
			const invoiceToId = selectedAgreement?.invoiceTo?.id;
			creditorInput = { id: invoiceToId ?? selectedContract.creditor?.id };
		} else if (mode === "manual") {
			if (manualCreditor?.id) {
				creditorInput = { id: manualCreditor.id };
			} else {
				creditorInput = { id: charter.creditor.id };
			}
		}

		saveInvoiceLine({
			variables: {
				input: {
					id: invoiceLine?.id || undefined,
					description: data.description,
					creditor: creditorInput,
					quantity: data.quantity,
					price: {
						value: data.priceValue,
						currencyIso: data.currency?.isoCode,
					},
					costTypeId: costType?.id,
					charterId: charter?.id,
					invoiceReference: invoiceLine?.invoiceReference,
					unexpected: data.unexpected,
					declarationId,
					agreementId: selectedAgreement?.id,
				},
			},
		}).then(() => {
			onClose();
			refetch();
		});
	};

	return (
		<FormDialog
			open={open}
			onClose={onClose}
			form="invoiceLineForm"
			maxWidth="sm"
			loading={saveLoading}>
			<React.Fragment>
				{!isEditing && (
					<Box sx={{ mb: 1 }}>
						<ToggleButtonGroup
							value={mode}
							exclusive
							onChange={(event, newValue) => {
								if (newValue !== null) {
									handleModeChange(newValue);
								}
							}}
							size="small"
							color="primary">
							<ToggleButton value="manual">
								Manual
							</ToggleButton>
							<ToggleButton value="supplier">
								Supplier
							</ToggleButton>
						</ToggleButtonGroup>
					</Box>
				)}

				<form id="invoiceLineForm" onSubmit={(event) => void handleSubmit(save)(event)}>

					<FormGroup label={Language("COST_TYPE")}
						labelPlacement="left-center"
						overflow="hidden"
						required>
						<FormField errors={formState.errors} name="costType">
							<Controller
								name="costType"
								control={control}
								rules={{ required: true }}
								render={({ field }) => (
									<CostTypeSelect
										value={field.value}
										onChange={handleCostTypeChange}
									/>
								)}
							/>
						</FormField>
					</FormGroup>

					{mode === "supplier" && (
						<>
							<FormGroup label={Language("SUPPLIER")}
								labelPlacement="left-center"
								overflow="hidden"
								required>
								<FormField errors={formState.errors} name="supplier">
									<Controller
										name="supplier"
										control={control}
										rules={{ required: true }}
										render={() => (
											<RelationSelect
												types={[ "SUPPLIER" ]}
												value={selectedSupplier}
												onChange={handleSupplierSelect}
											/>
										)}
									/>
								</FormField>
							</FormGroup>

							{selectedSupplier && (
								<FormGroup label={Language("CONTRACT")}
									labelPlacement="left-center"
									overflow="hidden"
									required>
									<FormField errors={formState.errors} name="contract">
										<Controller
											name="contract"
											control={control}
											rules={{ required: true }}
											render={() => (
												<Select
													key={selectedSupplier?.id}
													fetchOptions={fetchContracts}
													value={selectedContract}
													getOptionLabel={(opt) => `${opt.contractNumber} - ${opt.description}`}
													onChange={handleContractSelect}
												/>
											)}
										/>
									</FormField>
								</FormGroup>
							)}

							{selectedContract && (
								<FormGroup label={Language("AGREEMENT")}
									labelPlacement="left-center"
									overflow="hidden"
									required>
									<FormField errors={formState.errors} name="agreement">
										<Controller
											name="agreement"
											control={control}
											rules={{ required: true }}
											render={() => (
												<Select
													popoverHeight={500}
													key={selectedContract?.id}
													fetchOptions={fetchAgreements}
													value={selectedAgreement}
													getOptionLabel={(option) => option.summary}
													renderOption={(props, option) => (
														<li {...props}>
															<div style={{ width: "100%" }}>
																<div style={{ display: "block", whiteSpace: "wrap", width: "100%" }}>
																	{option.summary}
																</div>
															</div>
														</li>
													)}
													onChange={handleAgreementSelect}
												/>
											)}
										/>
									</FormField>
								</FormGroup>
							)}

							{selectedAgreement && (
								<>
									<FormGroup label={Language("DESCRIPTION")} labelPlacement="left-center" overflow="hidden">
										<Controller
											name="description"
											control={control}
											render={({ field }) => <Input {...field} />}
										/>
									</FormGroup>

									<FormGroup label={Language("INVOICE_QUANTITY")}
										labelPlacement="left-center"
										overflow="hidden"
										required>
										<FormField errors={formState.errors} name="quantity">
											<Controller
												name="quantity"
												control={control}
												rules={{ required: true }}
												render={({ field }) => (
													<Input
														{...field}
														type="number"
														step="any"
														onChange={(e) => {
															field.onChange(e);
															const quantity = e.target.value ? DataType.toBigDecimal(e.target.value) : 0;
															const price = getValues("priceValue");
															handleTotalPrice(quantity, DataType.toBigDecimal(price));
														}}
													/>
												)}
											/>
										</FormField>
									</FormGroup>

									<FormGroup label={Language("INVOICE_PRICE")} labelPlacement="left-center" overflow="hidden">
										<Controller
											name="priceValue"
											control={control}
											render={({ field }) => (
												<Input {...field}
													type="number"
													step="any"
													disabled
													addonBefore={currencySymbol} />
											)}
										/>
									</FormGroup>

									<FormGroup label={Language("TOTAL_EXCLUDE_VAT")} labelPlacement="left-center" overflow="hidden">
										<Controller
											name="totalPrice"
											control={control}
											render={({ field }) => (
												<Input {...field}
													type="number"
													disabled
													addonBefore={currencySymbol} />
											)}
										/>
									</FormGroup>
								</>
							)}
						</>
					)}

					{mode === "manual" && (
						<>
							<FormGroup label={Language("DESCRIPTION")} labelPlacement="left-center" overflow="hidden">
								<Controller
									name="description"
									control={control}
									placeholder={null}
									render={({ field }) => <Input {...field} autoFocus />}
								/>
							</FormGroup>

							<FormGroup label={Language("INVOICE_QUANTITY")}
								labelPlacement="left-center"
								overflow="hidden"
								required>
								<FormField errors={formState.errors} name="quantity">
									<Controller
										name="quantity"
										control={control}
										rules={{ required: true }}
										render={({ field }) => (
											<Input
												{...field}
												type="number"
												step="any"
												onChange={(e) => {
													field.onChange(e);
													const quantity = e.target.value ? DataType.toBigDecimal(e.target.value) : 0;
													const price = getValues("priceValue");
													handleTotalPrice(quantity, DataType.toBigDecimal(price));
												}}
											/>
										)}
									/>
								</FormField>
							</FormGroup>

							<FormGroup label={Language("INVOICE_PRICE")}
								labelPlacement="left-center"
								overflow="hidden"
								required>
								<FormField errors={formState.errors} name="priceValue">
									<Controller
										name="priceValue"
										control={control}
										rules={{ required: true }}
										render={({ field }) => (
											<Input
												{...field}
												type="number"
												step="any"
												disabled={!!selectedAgreement}
												addonBefore={currencySymbol}
												onChange={(e) => {
													field.onChange(e);
													const price = e.target.value ? DataType.toBigDecimal(e.target.value) : 0;
													const quantity = getValues("quantity");
													handleTotalPrice(DataType.toBigDecimal(quantity), price);
												}}
											/>
										)}
									/>
								</FormField>
							</FormGroup>

							<FormGroup label={Language("TOTAL_EXCLUDE_VAT")} labelPlacement="left-center" overflow="hidden">
								<Controller
									name="totalPrice"
									control={control}
									render={({ field }) => (
										<Input
											{...field}
											disabled
											type="number"
											addonBefore={currencySymbol}
											onChange={(e) => {
												field.onChange(e);
												const totalPrice = e.target.value;
												const quantity = getValues("quantity");
												if (!isNaN(Number(totalPrice)) && !isNaN(Number(quantity)) && Number(quantity) !== 0) {
													const price = Number(DataType.toBigDecimal(Number(totalPrice)) / Number(quantity));
													setValue("priceValue", !isNaN(price) ? price.toFixed(2) : undefined);
												}
											}}
										/>
									)}
								/>
							</FormGroup>

							{invoiceLine?.id && (
								<FormGroup label={Language("UNEXPECTED")} labelPlacement="left-center" overflow="hidden">
									<Controller
										name="unexpected"
										control={control}
										render={({ field }) => (
											<Checkbox {...field} checked={field.value} value={undefined} />
										)}
									/>
								</FormGroup>
							)}

							<FormGroup label={Language("CONTRACT_CURRENCY")} labelPlacement="left-center" overflow="hidden">
								<Controller
									name="currency"
									control={control}
									rules={{ required: true }}
									render={({ field }) => (
										<CurrencySelect
											value={field.value}
											onChange={(val) => {
												field.onChange(val);
												if (val) {
													setCurrencySymbol(val.symbol);
												}
											}}
											disabled={!!currentCurrency || !!selectedAgreement}
										/>
									)}
								/>
							</FormGroup>

							<FormGroup label={Language("CREDITOR")} labelPlacement="left-center" overflow="hidden">
								<RelationSelect
									types={[ "SUPPLIER" ]}
									value={manualCreditor}
									onChange={(val) => {
										setManualCreditor(val);
										setSelectedAgreement(undefined);
									}}
									placeholder={charter.creditor.name}
								/>
							</FormGroup>
						</>
					)}

				</form>
			</React.Fragment>
		</FormDialog>
	);
};

const getCurrencyDefaults = (invoiceLine, charter, contractCurrencies, currencies) => {
	const splitCurrencies = contractCurrencies?.split(";");
	const primaryCurrency = splitCurrencies?.length > 0 && splitCurrencies[splitCurrencies.length - 1];
	const defaultIsoCode = invoiceLine?.price?.currency?.isoCode || charter?.declarations?.[0]?.invoiceLines?.[0]?.price?.currency?.isoCode || primaryCurrency;
	const defaultSymbol = invoiceLine?.price?.currency?.symbol || charter?.declarations?.[0]?.invoiceLines?.[0]?.price?.currency?.symbol || primaryCurrency ? currencies.find(({ isoCode }) => isoCode == primaryCurrency)?.symbol : undefined;
	return { defaultIsoCode, defaultSymbol };
};

const CONTRACTS_QUERY = gql(`#graphql
	query getActiveSupplierContracts_invoiceLines($search: ContractSearch!, $pagination: Pagination) {
		contracts(search: $search, pagination: $pagination) {
			content {
				id
				contractNumber
				description
				creditor {
					id
					name
				}
				agreements(onlyDeclarableAgreements: true) {
					id
					description
					summary
					invoiceTo {
						id
						name
					}
					calculationMethod {
						price
					}
					costType {
						id
					}
				}
			}
		}
	}
`);

const mutation = gql(`#graphql
	mutation insertOrUpdateInvoiceLine_invoiceLines($input: InvoiceLineInput!) {
		insertOrUpdateInvoiceLine(invoiceLineInput: $input) {
			id
		}
	}
`);

const closeMutation = gql(`#graphql
	mutation close_invoiceLines($id: Long) {
		close(id: $id) {
			id
		}
	}
`);

const FINANCIALLY_READY_MUTATION = gql(`#graphql
	mutation readyFinancially_invoiceLines($id: Long) {
		readyFinancially(id: $id) {
			id
		}
	}
`);

export const FINANCIALLY_UNREADY_MUTATION = gql(`#graphql
	mutation unreadyFinancially_invoiceLines($id: Long) {
		unreadyFinancially(id: $id) {
			id
		}
	}
`);

const REVOKE_APPROVAL = gql(`#graphql
	mutation revokeInvoiceLineApproval_invoiceLines($invoiceLineIds: [Long]!) {
		revokeInvoiceLineApproval(invoiceLineIds: $invoiceLineIds)
	}
`);

const DELETE_LINE = gql(`#graphql
	mutation deleteInvoiceLine_invoiceLines($id: Long) {
		deleteInvoiceline(id: $id) {
			id
		}
	}
`);

const DELETE_DECLARATION = gql(`#graphql
	mutation deleteDeclaration_invoiceLines($id: Long) {
		deleteDeclaration(id: $id)
	}
`);

export default InvoiceLines;
